import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableContainer,
  Typography,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import LinearProgress from "@mui/material/LinearProgress";
import Snackbar from "@mui/material/Snackbar";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import TenantReportService from "../../Services/tenantReport.service";

const TenantInformationReport = () => {
  const { t } = useTranslation();
  const [state, setState] = useState({
    open: false,
    vertical: "top",
    horizontal: "center",
  });
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState(null);
  const [error, setError] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState(null);
  const { vertical, horizontal, open } = state;
  const handleClick = (newState) => () => {
    setState({ ...newState, open: true });
  };
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [filteredData, setFilteredData] = useState();

  const handleStatusFilterChange = (event) => {
    const value = event.target.value;
    setStatusFilter(value);
  };

  const handleClose = () => {
    setState({ ...state, open: false });
  };

  const fetchTenantReport = async (status) => {
    try {
      setLoading(true);
      const tenantReport = await TenantReportService.getAllReports({
        payment_status: status,
      });
      const sortedRequests = tenantReport.sort((a, b) => {
        if (a.created_at > b.created_at) {
          return -1;
        } else {
          return 1;
        }
      });
      setFilteredData(sortedRequests);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching maintenance requests:", error);
      setError(error.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenantReport(statusFilter);
  }, [statusFilter]);

  return (
    <div className="text-center">
      <Helmet>
        <title>SkyBMS - {t("title.tenantInformation")}</title>
      </Helmet>

      <>
        <div className="flex justify-between items-center py-7">
          <div className="flex flex-col gap-3">
            <Typography
              variant="h6"
              sx={{ color: "#909aab", alignSelf: "start" }}
            >
              {t("tenantInformationReport.reportTitle")}
            </Typography>
          </div>
        </div>
        <Grid container spacing={3}>
          {/* Filter Section */}
          <Grid item xs={12} md={4}>
            <div style={{ padding: 12, backgroundColor: "#fff" }}>
              <Typography
                variant="h6"
                sx={{ color: "#909aab", alignSelf: "start" }}
              >
                {t("tenantInformationReport.filterReports")}
              </Typography>

              <FormControl fullWidth margin="normal">
                <InputLabel>{t("tenantInformationReport.status")}</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  label={t("tenantInformationReport.status")}
                >
                  <MenuItem value="ALL">
                    {t("tenantInformationReport.all")}
                  </MenuItem>
                  <MenuItem value="paid">
                    {t("tenantInformationReport.paid")}
                  </MenuItem>
                  <MenuItem value="UNPAID">
                    {t("tenantInformationReport.unpaid")}
                  </MenuItem>
                </Select>
              </FormControl>
              {/* <Button variant="contained" color="inherit" fullWidth>
                Apply Filters
              </Button> */}
            </div>
          </Grid>

          <Grid item xs={12} md={8}>
            <div style={{ padding: 16, backgroundColor: "#fff" }}>
              <Typography
                variant="h6"
                sx={{ color: "#909aab", alignSelf: "start", paddingY: 1 }}
              >
                {t("tenantInformationReport.reportView")}
              </Typography>
              <TableContainer>
                <Box>
                  <Fade
                    in={loading}
                    style={{
                      transitionDelay: loading ? "100ms" : "0ms",
                    }}
                    unmountOnExit
                  >
                    <LinearProgress />
                  </Fade>
                </Box>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: "#909aab", alignSelf: "start" }}>
                        {t("tenantInformationReport.num")}
                      </TableCell>
                      <TableCell sx={{ color: "#909aab", alignSelf: "start" }}>
                        {t("tenantInformationReport.businessName")}
                      </TableCell>
                      <TableCell sx={{ color: "#909aab", alignSelf: "start" }}>
                        {t("tenantInformationReport.phoneNumber")}
                      </TableCell>
                      <TableCell sx={{ color: "#909aab", alignSelf: "start" }}>
                        {t("tenantInformationReport.ownerName")}
                      </TableCell>
                      <TableCell sx={{ color: "#909aab", alignSelf: "start" }}>
                        {t("tenantInformationReport.buildingUnit")}
                      </TableCell>
                      <TableCell sx={{ color: "#909aab", alignSelf: "start" }}>
                        {t("tenantInformationReport.monthlyRent")}
                      </TableCell>
                      <TableCell sx={{ color: "#909aab", alignSelf: "start" }}>
                        {t("tenantInformationReport.totalPayment")}
                      </TableCell>
                      <TableCell sx={{ color: "#909aab", alignSelf: "start" }}>
                        {t("tenantInformationReport.status")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          {t("maintenanceRequest.loading")}
                        </TableCell>
                      </TableRow>
                    ) : filteredData?.length !== 0 ? (
                      filteredData.flatMap((report, index) =>
                        report.payments.map((payment, paymentIndex) => (
                          <TableRow key={`${report.id}-${payment.id}`}>
                            {paymentIndex === 0 && (
                              <TableCell rowSpan={report.payments.length}>
                                {index + 1}
                              </TableCell>
                            )}
                            {paymentIndex === 0 && (
                              <TableCell rowSpan={report.payments.length}>
                                {report.tenant_business_name}
                              </TableCell>
                            )}
                            {paymentIndex === 0 && (
                              <TableCell rowSpan={report.payments.length}>
                                {report.tenant_business_phone_number}
                              </TableCell>
                            )}
                            {paymentIndex === 0 && (
                              <TableCell rowSpan={report.payments.length}>
                                {report.tenant_name}
                              </TableCell>
                            )}
                            {paymentIndex === 0 && (
                              <TableCell rowSpan={report.payments.length}>
                                {report.building_unit_number}
                              </TableCell>
                            )}
                            {paymentIndex === 0 && (
                              <TableCell rowSpan={report.payments.length}>
                                {report.monthly_rent}
                              </TableCell>
                            )}
                            <TableCell>{payment.total_payment}</TableCell>
                            <TableCell>{payment.status}</TableCell>
                          </TableRow>
                        ))
                      )
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          {t("tenantInformationReport.noDataFound")}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </Grid>
        </Grid>

        <div className="mt-8"></div>
        {successMessage && (
          <Box sx={{ width: 500 }}>
            <Snackbar
              anchorOrigin={{ vertical, horizontal }}
              open={open}
              onClose={handleClose}
              autoHideDuration={2000}
              key={vertical + horizontal}
            >
              <MuiAlert
                onClose={handleClose}
                severity="success"
                sx={{ width: "100%" }}
              >
                {successMessage}
              </MuiAlert>
            </Snackbar>
          </Box>
        )}
        {error && (
          <Box sx={{ width: 500 }}>
            <Snackbar
              anchorOrigin={{ vertical, horizontal }}
              open={open}
              onClose={handleClose}
              autoHideDuration={2000}
              key={vertical + horizontal}
            >
              <MuiAlert
                onClose={handleClose}
                severity="error"
                sx={{ width: "100%" }}
              >
                {error}
              </MuiAlert>
            </Snackbar>
          </Box>
        )}
      </>
      {/* )} */}
    </div>
  );
};

export default TenantInformationReport;
